import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJourney } from '../context/JourneyContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Search, Play, BookOpen, Clock, Lock, Sparkles, Filter, Info } from 'lucide-react';
import axios from 'axios';
import { cn } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Official Let's Siva logo
const WHITE_LOGO = "https://customer-assets.emergentagent.com/job_dance-confidence/artifacts/00kbs5d6_Lets%20Siva_Final%20Logo_Black.png";

const MODULES = ['All', 'Confidence', 'Connection', 'Hauora', 'Leadership', 'Unity & Belonging'];
const LEVELS = ['All', 'Primary', 'Intermediate', 'Secondary', 'Teacher'];
const TYPES = ['All', 'Video', 'Activity', 'Lesson', 'Reflection'];

const LSPlus = () => {
  const navigate = useNavigate();
  const { trackPageView, trackCtaClick } = useJourney();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedModule, setSelectedModule] = useState('All');
  const [selectedLevel, setSelectedLevel] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    trackPageView('ls_plus');
    fetchResources();
  }, [trackPageView]);

  const fetchResources = async () => {
    try {
      const response = await axios.get(`${API}/resources`);
      setResources(response.data);
    } catch (error) {
      console.error('Failed to fetch resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredResources = resources.filter(resource => {
    const matchesSearch = resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesModule = selectedModule === 'All' || resource.module === selectedModule;
    const matchesLevel = selectedLevel === 'All' || resource.level === selectedLevel;
    const matchesType = selectedType === 'All' || resource.resource_type === selectedType;
    
    return matchesSearch && matchesModule && matchesLevel && matchesType;
  });

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Video': return Play;
      case 'Activity': return Sparkles;
      case 'Lesson': return BookOpen;
      default: return BookOpen;
    }
  };

  return (
    <div 
      data-testid="ls-plus-screen"
      className="min-h-screen relative overflow-hidden bg-[#1C1917]"
    >
      {/* Background */}
      <div className="absolute top-40 right-0 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-0 w-72 h-72 bg-accent/5 rounded-full blur-3xl" />

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 py-12">
        {/* Navigation */}
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-8 text-stone-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Header */}
        <div className="text-center mb-12 animate-fade-in-up">
          <img 
            src={WHITE_LOGO}
            alt="Let's Siva"
            className="h-12 mx-auto mb-4 object-contain"
          />
          <Badge className="mb-4 bg-accent/20 text-accent border-accent/30">
            Let's Siva+
          </Badge>
          <h1 
            data-testid="ls-plus-title"
            className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-4"
          >
            Extend the Impact
          </h1>
          <p className="text-base sm:text-lg text-stone-400 max-w-2xl mx-auto">
            Access our library of resources to continue the movement journey beyond the session.
          </p>
        </div>

        {/* Future Access Signalling */}
        <div className="bg-accent/10 border border-accent/30 rounded-2xl p-4 mb-8 flex items-start gap-3 animate-fade-in-up">
          <Info className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
          <p className="text-sm text-stone-300">
            <strong className="text-accent">Expanding access:</strong> LS+ digital classroom resources coming soon for schools and teachers.
          </p>
        </div>

        {/* Trial CTA */}
        <div className="bg-stone-900/80 border border-stone-800 rounded-3xl p-6 md:p-8 mb-10 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-heading font-bold text-white mb-1">
                Try LS+ Free
              </h2>
              <p className="text-stone-400">
                Start your free trial and unlock all resources for 14 days.
              </p>
            </div>
            <Button
              data-testid="start-trial-btn"
              onClick={() => trackCtaClick('start_trial')}
              className="rounded-full px-8 py-6 bg-primary hover:bg-primary/90 text-white font-bold"
            >
              Start free trial
            </Button>
          </div>
        </div>

        {/* Search and filters */}
        <div className="mb-8 space-y-4 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-500" />
              <Input
                data-testid="search-input"
                placeholder="Search resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 rounded-xl h-12 bg-stone-900 border-stone-700 text-white placeholder:text-stone-500"
              />
            </div>
            <Button
              data-testid="filter-toggle-btn"
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="rounded-xl h-12 border-stone-700 text-stone-300 hover:border-primary/50 hover:bg-stone-800"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 rounded-2xl bg-stone-900/50 border border-stone-800">
              <Select value={selectedModule} onValueChange={setSelectedModule}>
                <SelectTrigger data-testid="module-filter" className="rounded-xl bg-stone-800 border-stone-700 text-stone-300">
                  <SelectValue placeholder="Module" />
                </SelectTrigger>
                <SelectContent>
                  {MODULES.map(module => (
                    <SelectItem key={module} value={module}>{module}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedLevel} onValueChange={setSelectedLevel}>
                <SelectTrigger data-testid="level-filter" className="rounded-xl bg-stone-800 border-stone-700 text-stone-300">
                  <SelectValue placeholder="Level" />
                </SelectTrigger>
                <SelectContent>
                  {LEVELS.map(level => (
                    <SelectItem key={level} value={level}>{level}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger data-testid="type-filter" className="rounded-xl bg-stone-800 border-stone-700 text-stone-300">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  {TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Resources grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResources.map((resource, index) => {
              const TypeIcon = getTypeIcon(resource.resource_type);
              const isLocked = resource.access_level === 'member';
              
              return (
                <Card
                  key={resource.id}
                  data-testid={`resource-card-${resource.id}`}
                  className={cn(
                    "bg-stone-900/50 border border-stone-800 p-6 transition-all duration-300 hover:border-primary/30 rounded-2xl animate-fade-in-up opacity-0",
                    isLocked && "opacity-70"
                  )}
                  style={{ animationDelay: `${0.3 + index * 0.05}s`, animationFillMode: 'forwards' }}
                >
                  {/* Type badge */}
                  <div className="flex items-center justify-between mb-4">
                    <Badge variant="outline" className="border-stone-700 text-stone-400">
                      <TypeIcon className="w-3 h-3 mr-1" />
                      {resource.resource_type}
                    </Badge>
                    {isLocked && (
                      <Lock className="w-4 h-4 text-stone-500" />
                    )}
                  </div>

                  {/* Content */}
                  <h3 className="text-lg font-heading font-bold text-white mb-2">
                    {resource.title}
                  </h3>
                  <p className="text-sm text-stone-400 mb-4">
                    {resource.description}
                  </p>

                  {/* Meta */}
                  <div className="flex items-center gap-4 text-sm text-stone-500">
                    <span className="flex items-center gap-1">
                      <BookOpen className="w-4 h-4" />
                      {resource.module}
                    </span>
                    {resource.duration && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {resource.duration}
                      </span>
                    )}
                  </div>

                  {/* Access level */}
                  <div className="mt-4 pt-4 border-t border-stone-800">
                    <Badge 
                      className={cn(
                        "text-xs",
                        resource.access_level === 'free' 
                          ? "bg-accent/20 text-accent" 
                          : "bg-primary/20 text-primary"
                      )}
                    >
                      {resource.access_level === 'free' ? 'Free' : 'Member'}
                    </Badge>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {filteredResources.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-stone-500">No resources found matching your filters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LSPlus;
